import { ContainerQuery } from '@/components/container-query/ContainerQuery';
import usePersistFn from '@/hooks/usePersistFn';
import { useAppearanceConfigModelStore } from '@/models/appearance-config/appearanceConfig';
import { useClientConfigModelStore } from '@/models/client-config/clientConfig';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import type { HttpApiServerType } from '@/services/http-api/httpApi';
import { DOCS_SITE_EVENT } from '@/shared/constant/Analytics';
import { ApiDocLayout } from '@/shared/constant/layout/ApiDocLayout';
import { OnlineType } from '@/shared/constant/OnlineType';
import type { ResourceHttpApiData } from '@/shared/constant/ResourceData';
import { gaReport } from '@/shared/util/analytics';
import { isContainerQueryFallback, isOldSafari } from '@/shared/util/ssr';
import { ModelType } from '@tarslib/types/lib/common';
import clsx from 'clsx';
import React from 'react';
import { ApiCustomFieldList } from '../api-custom-field-list/ApiCustomFieldList';
import { ApiStatus } from '../api-status/ApiStatus';
import { BottomBanner } from '../bottom-banner/BottomBanner';
import { DocsBreadcrumb } from '../docs-breadcrumb/DocsBreadcrumb';
import { EnvironmentUrl } from '../environment-url/EnvironmentUrl';
import { Footer } from '../footer/Footer';
import { LastModifiedAt } from '../last-modified-at/LastModifiedAt';
import { MarkdownViewer } from '../markdown-viewer/MarkdownViewer';
import { Paginator } from '../paginator/Paginator';
import { RequestParameter } from '../request-parameter/RequestParameter';
import { RequestSampleCode } from '../request-sample-code/RequestSampleCode';
import { ResourceHeader } from '../resource-header/ResourceHeader';
import { ResponseParameter } from '../response-parameter/ResponseParameter';
import { TagList } from '../tag-list/TagList';
import { RightSampleCode } from './components/right-sample-code/RightSampleCode';
import { useGenerateDefaultExample } from './hooks/useGenerateDefaultExample';
import { useIsShowRightSampleCode } from './hooks/useIsShowRightSampleCode';

export type HttpApiContentProps = {
  value: HttpApiServerType;
  extraData: ResourceHttpApiData['extraData'];
};

export function HttpApiContent(props: HttpApiContentProps) {
  const { value: valueProps, extraData } = props;

  const [containerSize, setContainerSize] = React.useState(0);
  const [isOldSafariRender, setIsOldSafariRender] = React.useState(false);
  const value = useGenerateDefaultExample(valueProps);
  const title = useDocsDataModelStore((x) => x.docsBaseConfig.title);
  const projectId = useDocsDataModelStore((x) => x.docsIdTypeData.projectId);
  const branchId = useDocsDataModelStore((x) => {
    if (x.docsIdTypeData.onlineType === OnlineType.SharedDoc) {
      return undefined;
    }
    return x.docsIdTypeData.branchId;
  });
  const appWebUrl = useClientConfigModelStore((x) => x.appWebUrl);
  const setIsOpenDebugPanel = useAppearanceConfigModelStore((x) => x.setIsOpenDebugPanel);
  const isOpenDebugPanel = useAppearanceConfigModelStore((x) => x.isOpenDebugPanel);
  const environmentUrlValue = React.useMemo(() => {
    return {
      type: ModelType.HttpApi as const,
      method: value.method,
      path: value.path,
    };
  }, [value]);

  const tagsValue = React.useMemo(() => {
    return value.tags ?? [];
  }, [value.tags]);

  const requestParameterValue = React.useMemo(() => {
    return {
      id: value.id,
      type: ModelType.HttpApi as const,
      parameters: value.parameters,
      requestBody: value.requestBody,
      auth: value.auth,
      securityScheme: value.securityScheme,
    };
  }, [value.parameters, value.requestBody, value.id, value.securityScheme, value.auth]);

  const handleTryItOut = usePersistFn(() => {
    gaReport(DOCS_SITE_EVENT.DEBUG_BTN, { page_title: document.title });
    setIsOpenDebugPanel(true);
  });
  const handleRunInApp = usePersistFn(() => {
    if (projectId) {
      let path = `${appWebUrl}/link/project/${projectId}/apis/api-${value.id}-run`;
      if (branchId) {
        const search = new URLSearchParams({ branchId: String(branchId) }).toString();
        path += `?${search}`;
      }
      gaReport(DOCS_SITE_EVENT.RUN_IN_APP, { page_title: title });
      window.open(path);
    }
  });
  const apiDocLayout = useDocsDataModelStore((x) => x.docsBaseConfig.apiDocLayout);
  const isShowRightSampleCode = useIsShowRightSampleCode(value);
  const hasRightSamplePadding = !isOpenDebugPanel && isShowRightSampleCode;

  React.useEffect(() => {
    if (isOldSafari) {
      setIsOldSafariRender(true);
    }
  }, []);

  return (
    <>
      <ContainerQuery
        containerSizes={[850, 1000, 1440]}
        className="flex @container/http-api-content os:pt-5"
        isContainerQueryFallback={isOldSafari || isContainerQueryFallback}
        onContainerSizeChange={(size) => {
          if (!isOldSafari) {
            return;
          }
          setContainerSize(size);
        }}
      >
        <div
          className={clsx('w-0 flex-1', {
            ['@[850px]/http-api-content:pr-[440px]']: hasRightSamplePadding,
          })}
        >
          <DocsBreadcrumb className="mb-2 hidden os:block" />
          <ResourceHeader
            name={value.name}
            className="mb-4"
            suffix={<ApiStatus className="ml-2" status={value.status} />}
          />

          <EnvironmentUrl
            value={environmentUrlValue}
            handleTryItOut={handleTryItOut}
            handleRunInApp={handleRunInApp}
            hasRightSamplePadding={hasRightSamplePadding}
          />

          <div className="h-6" />

          <TagList className="mb-2" tags={tagsValue} />

          <ApiCustomFieldList className="mb-6" />

          <MarkdownViewer value={value.description} inDocView enableHeaderPlugin className="mb-6" />

          {apiDocLayout === ApiDocLayout.TwoColumn && (
            <RightSampleCode
              className={clsx('mb-8', {
                ['@[850px]/http-api-content:mb-0 @[850px]/http-api-content:group-[.old-safari]/root:hidden']:
                  !isOpenDebugPanel,
              })}
              value={value}
              extraData={extraData}
            />
          )}

          <RequestParameter className="mb-8" value={requestParameterValue} />

          {apiDocLayout === ApiDocLayout.OneColumn && (
            <RequestSampleCode className="mb-8" value={value} extraData={extraData} />
          )}

          <ResponseParameter key={value.id} className="mb-8" value={value} />

          <BottomBanner className="mb-8" />

          <LastModifiedAt className="mb-14 mt-8" />

          <Paginator className="mb-10" />

          <Footer />
        </div>
      </ContainerQuery>

      {isOldSafariRender &&
        apiDocLayout === ApiDocLayout.TwoColumn &&
        !!containerSize &&
        !isOpenDebugPanel && (
          <div className="pointer-events-none fixed left-0 top-0 mx-[max(calc((100vw-1560px)/2),0px)] h-full w-full max-w-[1560px] @container/http-api-content">
            <RightSampleCode
              className="mb-0 @[850px]/http-api-content:group-[.old-safari]/root:mr-0"
              value={value}
              extraData={extraData}
            />
          </div>
        )}
    </>
  );
}
